import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import api from "../lib/api";
import { toast } from "sonner";
import { Download, ArrowLeft, Linkedin, ShieldCheck } from "lucide-react";
import CertificateDocument, { formatFrenchDate } from "../components/CertificateDocument";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function CertificateView() {
  const { id } = useParams();
  const [cert, setCert] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/certificates/${id}`).then((r) => setCert(r.data));
  }, [id]);

  const download = async () => {
    if (!cert || downloading) return;
    setDownloading(true);
    toast.message("Génération du PDF…");
    try {
      const blob = await pdf(
        <CertificateDocument
          userName={cert.user_full_name}
          certificationTitle={cert.certification_title}
          date={cert.issued_at}
          certificateId={cert.unique_code}
        />
      ).toBlob();
      const filename = `certificat_${slugify(cert.certification_title)}_${slugify(cert.user_full_name)}_${cert.unique_code}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Certificat téléchargé !");
    } catch (e) {
      console.error(e);
      toast.error("Impossible de générer le PDF. Réessayez.");
    } finally {
      setDownloading(false);
    }
  };

  const shareLinkedIn = () => {
    const verifyUrl = `${window.location.origin}/verify/${cert.unique_code}`;
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyUrl)}`;
    window.open(url, "_blank");
  };

  if (!cert) {
    return (
      <div className="p-10 text-center text-muted2 font-mono text-sm">Chargement…</div>
    );
  }

  const verifyUrl = `${window.location.origin}/verify/${cert.unique_code}`;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="certificate-view-page">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/certificates"
          className="text-xs uppercase tracking-widest text-gold-700 font-mono inline-flex items-center gap-1 hover:text-gold-600"
          data-testid="back-to-certificates"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Mes certificats
        </Link>
        <div className="flex gap-2">
          <button
            onClick={shareLinkedIn}
            className="px-4 py-2 rounded-full border border-gold-500/50 text-sm text-ink-900 hover:bg-gold-500/10 inline-flex items-center gap-2"
            data-testid="share-linkedin-button"
          >
            <Linkedin className="w-4 h-4" /> Partager
          </button>
          <button
            onClick={download}
            disabled={downloading}
            className="px-5 py-2 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-400 shadow-sm inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="download-pdf-button"
          >
            <Download className="w-4 h-4" /> {downloading ? "Génération…" : "Télécharger PDF"}
          </button>
        </div>
      </div>

      {/* Screen preview — mirror of the PDF (informational only, PDF is generated from template) */}
      <div className="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
        <CertificatePreview cert={cert} />
      </div>

      <div className="text-center text-xs text-muted2 mt-4 inline-flex items-center justify-center gap-2 w-full">
        <ShieldCheck className="w-3.5 h-3.5 text-gold-700" />
        Code public de vérification :{" "}
        <a href={verifyUrl} className="font-mono text-gold-700 hover:text-gold-600">
          {cert.unique_code}
        </a>
      </div>
    </div>
  );
}

/**
 * Screen preview that visually mirrors the react-pdf template.
 * Purely informational — the downloaded PDF is generated from CertificateDocument, not from this DOM.
 */
function CertificatePreview({ cert }) {
  const dateFr = formatFrenchDate(cert.issued_at);
  return (
    <div
      className="relative mx-auto bg-[#FDFDFB] rounded-lg overflow-hidden shadow-lg border border-[#E8E4DA]"
      style={{ aspectRatio: "297/210", width: "100%", minWidth: "720px", maxWidth: "1000px" }}
      data-testid="certificate-render"
    >
      {/* decorative frames */}
      <div className="absolute inset-[2.5%] border border-[#B08B3F]" />
      <div className="absolute inset-[3.6%] border border-[#DDBB78]/60" />

      <div className="absolute inset-[7%] flex flex-col items-center justify-between text-[#1B1B1D]">
        {/* Header */}
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-[#B08B3F]" />
            <span className="font-display text-[15px] sm:text-lg tracking-[0.25em] font-bold">
              AURUM ACADEMY
            </span>
            <span className="w-1 h-1 rounded-full bg-[#B08B3F]" />
          </div>
          <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.35em] text-[#8A8A8F] mt-1">
            Education · Certification · School
          </div>
          <div className="w-14 h-px bg-[#B08B3F] my-3" />
          <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.45em] text-[#B08B3F] font-medium">
            Certificat de réussite
          </div>
        </div>

        {/* Center */}
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
          <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.4em] text-[#4A4A4E] mb-3">
            Décerné à
          </div>
          <div
            className="font-display font-bold leading-tight text-[#1B1B1D]"
            style={{ fontSize: "clamp(22px, 4.2vw, 42px)" }}
            data-testid="cert-recipient"
          >
            {cert.user_full_name}
          </div>

          {/* Flourish */}
          <div className="flex items-center gap-1 mt-3 opacity-90">
            <span className="block h-px w-10 bg-[#DDBB78]" />
            <span className="block w-2 h-2 rotate-45 bg-[#B08B3F]" />
            <span className="block h-px w-10 bg-[#DDBB78]" />
          </div>

          <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.35em] text-[#8A8A8F] mt-5">
            a complété avec succès le parcours
          </div>
          <div
            className="font-display italic mt-2 text-[#1B1B1D] max-w-xl leading-snug"
            style={{ fontSize: "clamp(14px, 2.2vw, 22px)" }}
          >
            « {cert.certification_title} »
          </div>
          <p className="text-[10px] sm:text-xs text-[#4A4A4E] mt-3 max-w-md leading-relaxed">
            en satisfaisant à l'ensemble des évaluations requises par Aurum Academy.
          </p>
        </div>

        {/* Footer */}
        <div className="w-full flex items-end justify-between">
          <div>
            <div className="text-[7px] sm:text-[8px] uppercase tracking-[0.3em] text-[#8A8A8F]">
              Date d'obtention
            </div>
            <div className="font-display text-sm sm:text-base text-[#1B1B1D] mt-1" data-testid="cert-date">
              {dateFr}
            </div>
          </div>
          <div className="flex flex-col items-end">
            <div className="w-32 sm:w-40 h-px bg-[#1B1B1D] mb-1" />
            <div className="font-display font-bold text-xs sm:text-sm tracking-wide">Aurum Academy</div>
            <div className="text-[7px] sm:text-[8px] uppercase tracking-[0.3em] text-[#8A8A8F] mt-0.5">
              Autorité de certification
            </div>
          </div>
        </div>
      </div>

      {/* Certificate ID — inside decorative frame */}
      <div className="absolute left-0 right-0 flex justify-center items-center gap-1.5" style={{ bottom: "4.5%" }}>
        <span className="text-[7px] sm:text-[8px] uppercase tracking-[0.25em] text-[#8A8A8F]">
          Numéro de certificat
        </span>
        <span className="text-[8px] sm:text-[9px] tracking-[0.15em] text-[#4A4A4E] font-medium" data-testid="cert-code">
          {cert.unique_code}
        </span>
      </div>
    </div>
  );
}
